import { withLayouts } from 'react-dx'
import { useRouter } from 'next/router'
import { Spin } from 'antd'
import { Trans, useLingui } from '@lingui/react/macro'
import { P, match } from 'ts-pattern'

import { ListBlock } from '../../_components/ListBlock'

import { pushMusics } from './helpers'

import type { InferGetServerSidePropsType } from 'next'
import type { TNextPagePro } from '@/types/global'
import type { MusicListDataItem } from '@/pages/tad.ai/ai-music-generator/_types/trpc'

import { getServerSideData } from '@/helpers/server-data'
import { NotFoundError } from '@/helpers/server-data/errors'
import { FluentFlex, FluentFlexRoot } from '@/components/FluentFlex'
import { paddingHeaderHeightCls } from '@/pages/tad.ai/_layout/Header/constants'
import RootLayout from '@/pages/tad.ai/_layout/layouts/RootLayout'
import LayoutsDefaultLayout from '@/pages/tad.ai/_layout'
import SiteSpecificContextLayout from '@/pages/tad.ai/_layout/layouts/SiteSpecificContextLayout'
import AntdLayout from '@/pages/tad.ai/_layout/layouts/AntdLayout'


import { useGlobalAudioPlayer } from '@/hooks/audio/store';
import { useState, useRef, useEffect } from 'react';
import { api } from '@/utils/api';
import { cls } from 'tagged-classnames-free';
import Link from '@/components/Link';
import { useConfig } from '@/contexts/SiteContext/hooks';
const PAGE_SIZE = 30
type IdType = 'hot' | 'category' | 'template'
export const Page: TNextPagePro<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const router = useRouter()
  const { t } = useLingui()
  const slugs = router.query.slug
  const { collapsed } = useGlobalAudioPlayer()
  const [current, setCurrent] = useState(1)
  const idType = slugs![0] as IdType
  const [musics, setScrollMusicList] = useState<MusicListDataItem[]>([])
  const loadingRef = useRef<HTMLDivElement>(null)
  const [hasMore, setHasMore] = useState(true)
  const { isLoading: isMusicListLoading, isFetching: isMusicListFetching } =
    api.music_v2.getMusicList.useQuery(
      {
        pageNumber: current,
        pageSize: PAGE_SIZE,
        [`${idType}Id`]: Number(slugs?.[1]) ? Number(slugs?.[1]) : undefined,
      },
      {
        enabled: idType === 'template' || idType === 'category',
        onSuccess: (data) => {
          setHasMore(data.musics.length >= PAGE_SIZE)
          setScrollMusicList((prev) => pushMusics(prev, data.musics))
        },
      },
    )
  const { isLoading: isHotListLoading, isFetching: isHotListFetching } =
    api.music_v2.getHotList.useQuery(
      {
        pageNumber: current,
        pageSize: PAGE_SIZE,
      },
      {
        enabled: idType === 'hot',
        onSuccess: (data) => {
          setHasMore(data.musics.length >= PAGE_SIZE)
          setScrollMusicList((prev) => pushMusics(prev, data.musics))
        },
      },
    )

  const title = match(idType)
    .with('hot', () => t`Trend`)
    .with(
      P.union('category', 'template'),
      () => (router.query.name || '') as string,
    )
    .otherwise(() => '')
  const isPageLoading =
    idType === 'hot'
      ? isHotListLoading || isHotListFetching
      : isMusicListLoading || isMusicListFetching
  const showInitialLoading = musics.length === 0 && isPageLoading
  const renderLoading = musics.length > 0 && hasMore

  useEffect(() => {
    const target = loadingRef.current
    if (!target || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isPageLoading && hasMore) {
            setCurrent((current) => current + 1)
          }
        })
      },
      {
        threshold: 0.1, // 当元素 10% 可见时触发（默认 0，即刚进入视口）
        rootMargin: '0px 0px 282px 0px', // 扩展或缩小视口的检测范围
      },
    )
    observer.observe(target)
    return () => {
      observer.unobserve(target)
    }
  }, [hasMore, isPageLoading])

  return (
    <FluentFlexRoot>
      <FluentFlex
        className={`min-h-screen xl:h-screen ${paddingHeaderHeightCls}`}
      >
        <FluentFlex
          className={cls`
            flex-col px-6 pt-6 ${collapsed ? 'pb-6' : 'pb-32 xl:pb-24'} mx-auto
            gap-11 xl:px-[max(24px,calc(50%-800px+24px))]
          `}
        >
          <div className={cls`flex`}>
            <Link
              href={'/homepage'}
              className={cls`text-f-text-quaternary hover:text-f-text flex cursor-pointer items-center gap-2 text-[18px]`}
            >
              <span className={cls`i-cus--tad-left size-8`} />
              <Trans>Back</Trans>
            </Link>
          </div>
          {showInitialLoading ? (
            <div
              className={cls`flex min-h-[240px] items-center justify-center`}
            >
              <Spin spinning />
            </div>
          ) : (
            <ListBlock title={title} musics={musics} />
          )}
          <div
            className={`flex justify-center ${renderLoading ? '' : 'hidden'}`}
            ref={loadingRef}
          >
            <Spin spinning={isPageLoading} />
          </div>
        </FluentFlex>
      </FluentFlex>
    </FluentFlexRoot>
  )
}

Page.noPageKey = true

const LayoutsPage = withLayouts(
  Page,
  [
    (props) => {
      return (
        <LayoutsDefaultLayout
          {...props}
          main={{
            className: cls`bg-f-other-1`,
            includingHeader: true,
          }}
          sideBar={{
            className: '',
          }}
          footer={null}
        />
      )
    },
    (props) => {
      const { seoTitle } = useConfig()

      return (
        <RootLayout
          seo={{
            title: seoTitle('Home - template'),
            description:
              'Generate custom, royalty-free, unique songs and instrumentals with Tad AI. Create music for videos, podcasts, and commercial use in seconds.',
          }}
          {...props}
        />
      )
    },
    (props) => {
      return <SiteSpecificContextLayout {...props} />
    },
    AntdLayout,
  ],
  {
    propertiesHoist: ['noPageKey'],
  },
)

export default LayoutsPage

export const getServerSideProps = getServerSideData({
  cacheControl: true,
})(async (_, ctx, helpers) => {
  const trpcHelpers = await helpers.getTrpcHelpers()

  const slugs = ctx.params?.slug
  const idType = slugs?.[0]

  // 添加404检查逻辑
  const validIdTypes = ['hot', 'category', 'template']
  if (!idType || !validIdTypes.includes(idType)) {
    throw new NotFoundError('Invalid idType parameter')
  }

  if (Array.isArray(slugs)) {
    if (['template', 'category'].includes(slugs[0])) {
      const id = Number(slugs[1])
      if (!Number.isNaN(id)) {
        await trpcHelpers.music_v2.getMusicList.prefetch({
          pageNumber: 1,
          pageSize: PAGE_SIZE,
          [`${slugs[0]}Id`]: id,
        })
      }
    } else if (slugs[0] === 'hot') {
      await trpcHelpers.music_v2.getHotList.prefetch({
        pageNumber: 1,
        pageSize: PAGE_SIZE,
      })
    }
  }

  return { trpcState: trpcHelpers.dehydrate() }
})
